import React, { useState, useEffect } from 'react';
import { fattureAPI, commesseAPI, clientiAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  FileText,
  Euro,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Trash2,
  Pencil,
  TrendingUp,
  AlertCircle,
  Receipt
} from 'lucide-react';

const TIPO_FATTURAZIONE_OPTIONS = [
  { value: 'a_corpo', label: 'A Corpo' },
  { value: 'milestone', label: 'Milestone' },
  { value: 't_and_m', label: 'Time & Materials' },
];

const STATO_FATTURA_OPTIONS = [
  { value: 'bozza', label: 'Bozza', color: 'secondary' },
  { value: 'emessa', label: 'Emessa', color: 'default' },
  { value: 'pagata', label: 'Pagata', color: 'success' },
  { value: 'in_ritardo', label: 'In Ritardo', color: 'destructive' },
  { value: 'annullata', label: 'Annullata', color: 'outline' },
];

const getStatoBadge = (stato, giorniScadenza) => {
  // Override to show in_ritardo if past due
  if (stato !== 'pagata' && stato !== 'annullata' && giorniScadenza !== null && giorniScadenza < 0) {
    return <Badge variant="destructive">In Ritardo ({Math.abs(giorniScadenza)}g)</Badge>;
  }
  
  const opt = STATO_FATTURA_OPTIONS.find(o => o.value === stato);
  const variant = opt?.color === 'success' ? 'default' : opt?.color || 'secondary';
  return (
    <Badge variant={variant} className={opt?.color === 'success' ? 'bg-emerald-600' : ''}>
      {opt?.label || stato}
    </Badge>
  );
};

export const RicaviPage = () => {
  const { user } = useAuth();
  const [fatture, setFatture] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [summary, setSummary] = useState(null);
  const [agingReport, setAgingReport] = useState(null);
  const [alertScadenze, setAlertScadenze] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFattura, setEditingFattura] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('fatture');
  const [filterCommessa, setFilterCommessa] = useState('all');
  const [filterStato, setFilterStato] = useState('all');

  const [formData, setFormData] = useState({
    commessa_id: '',
    cliente_id: '',
    numero_fattura: '',
    data_fattura: '',
    data_scadenza: '',
    importo: '',
    tipo_fatturazione: 'a_corpo',
    stato: 'bozza',
    descrizione: '',
    note: '',
    ore_fatturate: '',
    tariffa_applicata: ''
  });

  const canAccess = user?.ruolo !== 'risorsa';
  const canDelete = user?.ruolo === 'admin' || user?.ruolo === 'coordinator';

  useEffect(() => {
    if (canAccess) {
      loadData();
    }
  }, [canAccess, filterCommessa, filterStato]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filterCommessa !== 'all') params.commessa_id = filterCommessa;
      if (filterStato !== 'all') params.stato = filterStato;
      
      const [fattureRes, commesseRes, clientiRes, summaryRes, agingRes, alertRes] = await Promise.all([
        fattureAPI.getAll(params),
        commesseAPI.getAll(),
        clientiAPI.getAll(),
        fattureAPI.getSummary(filterCommessa !== 'all' ? filterCommessa : null),
        fattureAPI.getAgingReport(),
        fattureAPI.getAlertScadenze(14)
      ]);
      
      setFatture(fattureRes.data);
      setCommesse(commesseRes.data.filter(c => c.stato === 'attiva'));
      setClienti(clientiRes.data);
      setSummary(summaryRes.data);
      setAgingReport(agingRes.data);
      setAlertScadenze(alertRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingFattura(null);
    const today = new Date().toISOString().split('T')[0];
    const scadenza = new Date();
    scadenza.setDate(scadenza.getDate() + 30);
    
    setFormData({
      commessa_id: filterCommessa !== 'all' ? filterCommessa : '',
      cliente_id: '',
      numero_fattura: '',
      data_fattura: today,
      data_scadenza: scadenza.toISOString().split('T')[0],
      importo: '',
      tipo_fatturazione: 'a_corpo',
      stato: 'bozza',
      descrizione: '',
      note: '',
      ore_fatturate: '',
      tariffa_applicata: ''
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (fattura) => {
    setEditingFattura(fattura);
    setFormData({
      commessa_id: fattura.commessa_id,
      cliente_id: fattura.cliente_id,
      numero_fattura: fattura.numero_fattura,
      data_fattura: fattura.data_fattura,
      data_scadenza: fattura.data_scadenza,
      importo: fattura.importo.toString(),
      tipo_fatturazione: fattura.tipo_fatturazione,
      stato: fattura.stato,
      descrizione: fattura.descrizione || '',
      note: fattura.note || '',
      ore_fatturate: fattura.ore_fatturate?.toString() || '',
      tariffa_applicata: fattura.tariffa_applicata?.toString() || ''
    });
    setIsDialogOpen(true);
  };

  const handleCommessaChange = (commessaId) => {
    setFormData(prev => ({ ...prev, commessa_id: commessaId }));
    // Auto-select cliente from commessa
    const commessa = commesse.find(c => c.id === commessaId);
    if (commessa?.cliente_id) {
      setFormData(prev => ({ ...prev, cliente_id: commessa.cliente_id }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.commessa_id || !formData.cliente_id || !formData.numero_fattura || !formData.importo) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        commessa_id: formData.commessa_id,
        cliente_id: formData.cliente_id,
        numero_fattura: formData.numero_fattura,
        data_fattura: formData.data_fattura,
        data_scadenza: formData.data_scadenza,
        importo: parseFloat(formData.importo),
        tipo_fatturazione: formData.tipo_fatturazione,
        stato: formData.stato,
        descrizione: formData.descrizione || null,
        note: formData.note || null,
        ore_fatturate: formData.ore_fatturate ? parseFloat(formData.ore_fatturate) : null,
        tariffa_applicata: formData.tariffa_applicata ? parseFloat(formData.tariffa_applicata) : null
      };

      if (editingFattura) {
        await fattureAPI.update(editingFattura.id, submitData);
        toast.success('Fattura aggiornata');
      } else {
        await fattureAPI.create(submitData);
        toast.success('Fattura creata');
      }

      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving fattura:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message).join(', ') 
        : (typeof detail === 'string' ? detail : 'Errore nel salvataggio');
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (fatturaId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa fattura?')) return;
    
    try {
      await fattureAPI.delete(fatturaId);
      toast.success('Fattura eliminata');
      loadData();
    } catch (error) {
      console.error('Error deleting fattura:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Errore nell\'eliminazione';
      toast.error(errorMsg);
    }
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Non hai i permessi per visualizzare questa pagina</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="ricavi-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const hasScadenze = alertScadenze && (alertScadenze.scadute?.length > 0 || alertScadenze.in_scadenza?.length > 0);

  return (
    <div className="space-y-6" data-testid="ricavi-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Ricavi & Fatturazione</h1>
          <p className="text-muted-foreground">Gestisci fatture e monitora i ricavi delle commesse</p>
        </div>
        <Button onClick={openAddDialog} data-testid="add-fattura-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuova Fattura
        </Button>
      </div>

      {/* Alert Scadenze */}
      {hasScadenze && (
        <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  Attenzione: {alertScadenze.scadute?.length || 0} fatture scadute, {alertScadenze.in_scadenza?.length || 0} in scadenza
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Totale scaduto: {formatCurrency(alertScadenze.totale_scadute || 0)} | 
                  In scadenza (14gg): {formatCurrency(alertScadenze.totale_in_scadenza || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Totale Fatturato</p>
                  <p className="text-xl font-bold font-mono">{formatCurrency(summary.totale_fatturato)}</p>
                  <p className="text-xs text-muted-foreground">{summary.fatture_emesse} fatture</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Incassato</p>
                  <p className="text-xl font-bold font-mono text-emerald-600">{formatCurrency(summary.totale_incassato)}</p>
                  <p className="text-xs text-muted-foreground">{summary.fatture_pagate} pagate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Da Incassare</p>
                  <p className="text-xl font-bold font-mono">{formatCurrency(summary.totale_da_incassare)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">In Ritardo</p>
                  <p className="text-xl font-bold font-mono text-red-600">{formatCurrency(summary.totale_in_ritardo)}</p>
                  <p className="text-xs text-muted-foreground">{summary.fatture_in_ritardo} fatture</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="fatture">Fatture</TabsTrigger>
          <TabsTrigger value="aging">Aging Report</TabsTrigger>
        </TabsList>

        {/* Fatture Tab */}
        <TabsContent value="fatture">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label className="mb-2 block">Filtra per Commessa</Label>
                  <Select value={filterCommessa} onValueChange={setFilterCommessa}>
                    <SelectTrigger data-testid="filter-commessa">
                      <SelectValue placeholder="Tutte le commesse" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le commesse</SelectItem>
                      {commesse.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.codice_commessa} - {c.nome_progetto}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="mb-2 block">Filtra per Stato</Label>
                  <Select value={filterStato} onValueChange={setFilterStato}>
                    <SelectTrigger data-testid="filter-stato">
                      <SelectValue placeholder="Tutti gli stati" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti gli stati</SelectItem>
                      {STATO_FATTURA_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {fatture.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nessuna fattura trovata</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N. Fattura</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Commessa</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fatture.map((f) => (
                      <TableRow key={f.id} data-testid={`fattura-row-${f.id}`}>
                        <TableCell className="font-mono font-medium">{f.numero_fattura}</TableCell>
                        <TableCell className="text-sm">{f.data_fattura}</TableCell>
                        <TableCell>
                          <div>
                            <span className="font-mono text-xs text-muted-foreground">{f.commessa_codice}</span>
                            <p className="text-sm">{f.commessa_nome}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{f.cliente_nome}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {TIPO_FATTURAZIONE_OPTIONS.find(o => o.value === f.tipo_fatturazione)?.label || f.tipo_fatturazione}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          {formatCurrency(f.importo)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {f.data_scadenza}
                          {f.giorni_scadenza !== null && f.giorni_scadenza !== undefined && f.stato !== 'pagata' && (
                            <span className={`text-xs block ${f.giorni_scadenza < 0 ? 'text-red-500' : f.giorni_scadenza <= 7 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                              {f.giorni_scadenza < 0 ? `${Math.abs(f.giorni_scadenza)}g scaduta` : `${f.giorni_scadenza}g`}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{getStatoBadge(f.stato, f.giorni_scadenza)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditDialog(f)}
                              data-testid={`edit-fattura-${f.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {canDelete && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleDelete(f.id)}
                                data-testid={`delete-fattura-${f.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aging Report Tab */}
        <TabsContent value="aging">
          {agingReport && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Aging Report - Fatture Non Pagate</CardTitle>
                  <CardDescription>
                    Totale scaduto: <span className="text-red-600 font-bold">{formatCurrency(agingReport.totale_scaduto)}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {[
                      { data: agingReport.current, color: 'emerald' },
                      { data: agingReport.bucket_1_30, color: 'amber' },
                      { data: agingReport.bucket_31_60, color: 'orange' },
                      { data: agingReport.bucket_61_90, color: 'red' },
                      { data: agingReport.bucket_over_90, color: 'rose' },
                    ].map((bucket, idx) => (
                      <Card key={idx} className={bucket.data.importo > 0 && idx > 0 ? `border-${bucket.color}-500` : ''}>
                        <CardContent className="p-4 text-center">
                          <p className="text-sm text-muted-foreground">{bucket.data.label}</p>
                          <p className={`text-xl font-bold font-mono ${idx > 0 && bucket.data.importo > 0 ? `text-${bucket.color}-600` : ''}`}>
                            {formatCurrency(bucket.data.importo)}
                          </p>
                          <p className="text-xs text-muted-foreground">{bucket.data.count} fatture</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="fattura-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingFattura ? 'Modifica Fattura' : 'Nuova Fattura'}
            </DialogTitle>
            <DialogDescription>
              {editingFattura ? 'Modifica i dati della fattura' : 'Inserisci i dati della nuova fattura'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="commessa">Commessa *</Label>
                <Select value={formData.commessa_id} onValueChange={handleCommessaChange}>
                  <SelectTrigger id="commessa" data-testid="fattura-commessa">
                    <SelectValue placeholder="Seleziona commessa" />
                  </SelectTrigger>
                  <SelectContent>
                    {commesse.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.codice_commessa} - {c.nome_progetto}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cliente">Cliente *</Label>
                <Select 
                  value={formData.cliente_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, cliente_id: v }))}
                >
                  <SelectTrigger id="cliente" data-testid="fattura-cliente">
                    <SelectValue placeholder="Seleziona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clienti.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="numero">N. Fattura *</Label>
                <Input
                  id="numero"
                  value={formData.numero_fattura}
                  onChange={(e) => setFormData(prev => ({ ...prev, numero_fattura: e.target.value }))}
                  placeholder="FT-2026-001"
                  required
                  data-testid="fattura-numero"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data">Data Fattura *</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data_fattura}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_fattura: e.target.value }))}
                  required
                  data-testid="fattura-data"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scadenza">Scadenza *</Label>
                <Input
                  id="scadenza"
                  type="date"
                  value={formData.data_scadenza}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_scadenza: e.target.value }))}
                  required
                  data-testid="fattura-scadenza"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="importo">Importo (€) *</Label>
                <Input
                  id="importo"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.importo}
                  onChange={(e) => setFormData(prev => ({ ...prev, importo: e.target.value }))}
                  placeholder="0.00"
                  required
                  data-testid="fattura-importo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo Fatturazione *</Label>
                <Select 
                  value={formData.tipo_fatturazione} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, tipo_fatturazione: v }))}
                >
                  <SelectTrigger id="tipo" data-testid="fattura-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPO_FATTURAZIONE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stato">Stato *</Label>
                <Select 
                  value={formData.stato} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, stato: v }))}
                >
                  <SelectTrigger id="stato" data-testid="fattura-stato">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATO_FATTURA_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.tipo_fatturazione === 't_and_m' && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-muted rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="ore">Ore Fatturate</Label>
                  <Input
                    id="ore"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.ore_fatturate}
                    onChange={(e) => setFormData(prev => ({ ...prev, ore_fatturate: e.target.value }))}
                    placeholder="0"
                    data-testid="fattura-ore"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tariffa">Tariffa Applicata (€/h)</Label>
                  <Input
                    id="tariffa"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.tariffa_applicata}
                    onChange={(e) => setFormData(prev => ({ ...prev, tariffa_applicata: e.target.value }))}
                    placeholder="0.00"
                    data-testid="fattura-tariffa"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="descrizione">Descrizione</Label>
              <Textarea
                id="descrizione"
                value={formData.descrizione}
                onChange={(e) => setFormData(prev => ({ ...prev, descrizione: e.target.value }))}
                placeholder="Descrizione della fattura..."
                rows={2}
                data-testid="fattura-descrizione"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                value={formData.note}
                onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Note interne..."
                rows={2}
                data-testid="fattura-note"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="fattura-submit">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingFattura ? 'Salva Modifiche' : 'Crea Fattura'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RicaviPage;
